import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";

interface UnsavedChangesDialogProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSave?: () => void;
  loading?: boolean;
  message?: string;
}

const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  open,
  onClose,
  onDiscard,
  onSave,
  loading = false,
  message = "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#fff3e0",
          borderBottom: "1px solid #ffcc80",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 2,
        }}
      >
        <WarningIcon sx={{ color: "#f57c00", fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#e65100" }}>
          Unsaved Changes
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Typography
            variant="body1"
            sx={{ color: "#424242", lineHeight: 1.6 }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: "#fafafa",
          borderTop: "1px solid #e0e0e0",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#9e9e9e",
            color: "#616161",
            "&:hover": {
              borderColor: "#757575",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Stay on Page
        </Button>
        <Button
          onClick={onDiscard}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{
            backgroundColor: "#f44336",
            "&:hover": {
              backgroundColor: "#d32f2f",
            },
          }}
        >
          Discard & Leave
        </Button>
        {onSave && (
          <Button
            onClick={onSave}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#4caf50",
              "&:hover": {
                backgroundColor: "#45a049",
              },
            }}
          >
            {loading ? "Saving..." : "Save & Leave"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UnsavedChangesDialog;
